import React from 'react';
import { useContextSelector } from 'use-context-selector';

import LoadingOverlay from '../../../../components/LoadingOverlay';
import ServiceItemAddCart from './ServiceItemAddCart';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { ModalsContext } from '../../../../contexts/ModalsContext';
import { ModuleContext } from '../../../../contexts/ModuleContext';
import { PatientContext } from '../../../../contexts/PatientContext';
import { PaymentContext } from '../../../../contexts/PaymentContext';
import { PlatformContext } from '../../../../contexts/PlatformContext';
import { RouterContext } from '../../../../contexts/RouterContext';
import { ServiceContext } from '../../../../contexts/ServiceContext';
import { usePatientModule } from '../../../../hooks/usePatientModule';
import { ProductItemProps } from '../../../../models/Product';
import { ServiceItemProps } from '../../../../models/Service';
import { fetchProductById, fetchServiceById } from '../../../../modules/products';
import { PAGES } from '../../../../Router/pages';
import { hasProductEnv, hasServiceEnv } from '../../../../utils/enviroment';
import { gtmEvent } from '../../../../utils/gtm';

import * as S from './styles';
import { INFO_GENERIC_RULES_CONDITION, INFO_GENERIC_RULES_WHEN } from '../../../../utils/constants';
import { ModalGenericRules } from '../../../../components/ModalGenericRules';
import { CartContext } from '../../../../contexts/CartContext';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { useMainRouter } from '../../../../Router/pageComponents';

type ResourceDetailsProps = {
  onClose?: () => void;
  setIsOpen?: (value: boolean) => void;
};

const ResourceDetails = ({ onClose }: ResourceDetailsProps) => {
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { changeProduct } = useContextSelector(CartContext, state => state);
  const { setSeletedService } = useContextSelector(ServiceContext, state => state);
  const { goTo, state, goBack, history } = useContextSelector(RouterContext, state => state);
  const { history: mainHistory, goBack: onMainBack } = useMainRouter();

  const {
    selectedItem,
    changeSchedule,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  } = useContextSelector(ScheduleContext, state => state);
  const { setIsSchedulerOpen, clearData } = useContextSelector(PlatformContext, state => state);
  const { patientSelected, addPatientList } = useContextSelector(PatientContext, state => state);
  const { eligibilityPlans, setEligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { selectedProduct, selectItemToSchedule, handleSelectProduct, selectedService, selectedItemToSchedule } =
    useContextSelector(ModuleContext, state => state);
  const [product, setProduct] = React.useState<ProductItemProps>();
  const [service, setService] = React.useState<ServiceItemProps>();
  const [loading, setLoading] = React.useState(false);

  const { createSchedule, genericModal, isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible } =
    usePatientModule({
      addPatientList,
      changeProduct,
      eligibilityPlans,
      setEligibilityPlans,
      goTo,
      changeSchedule,
      selectedSchedule: selectedItem,
      openModal,
      scheduleSpecialistSelected,
      setScheduleSpecialistSelected,
      changeScheduleSpecialist
    });

  const handleAddProductList = async ({
    product,
    service
  }: {
    product: ProductItemProps | undefined;
    service: ServiceItemProps | undefined;
  }) => {
    setLoading(true);
    if (product) {
      gtmEvent({
        event: 'agendamento-einstein',
        categoria: 'Agendar-Vacina',
        acao: 'step-2',
        rotulo: 'visualizou-detalhes'
      });

      handleSelectProduct(product);
      selectItemToSchedule(selectedItemToSchedule);
      if (!patientSelected) {
        goTo(PAGES.PATIENT_FORM.id);
      }
      if (patientSelected) {
        const hasModal = await createSchedule({
          product,
          patient: patientSelected,
          itemToSchedule: selectedItemToSchedule
        });

        if (hasModal) {
          if (hasModal?.type === INFO_GENERIC_RULES_CONDITION.is_first_appointment) {
            genericModal.setGenericModalCondition(INFO_GENERIC_RULES_CONDITION.is_first_appointment);
            genericModal.setModalGenericVisible(true);
          }
        }
      }
    }

    if (service) {
      gtmEvent({
        event: 'agendamento-einstein',
        categoria: 'Agendar-Cuidado',
        acao: 'step-2',
        rotulo: 'visualizou-detalhes'
      });
      if (service.id === process.env.REACT_APP_TELEORIENTATION_PRODUCT_ID) {
        const [product] = service.products;
        handleSelectProduct(product);
        selectItemToSchedule('PRODUCT');
        if (service.products.length === 1 && patientSelected) {
          await createSchedule({
            product,
            patient: patientSelected,
            itemToSchedule: 'PRODUCT'
          });
        }
        if (service.products.length === 1 && !patientSelected) {
          goTo(PAGES.PATIENT_FORM.id);
        }
      } else {
        onClose && onClose();
        if (service.id !== process.env.REACT_APP_CHECKUP_PRODUCT_ID) {
          setSeletedService && setSeletedService(service);
        }
        goTo(PAGES.PATIENT_FORM.id, {
          previousPage: !!state.previousPage,
          patientSelected
        });
      }
      setIsSchedulerOpen(true);
    }
    setLoading(false);
  };

  const getVaccine = async () => {
    setLoading(true);
    const productId = hasProductEnv();
    if (productId) {
      const result = await fetchProductById(productId);
      const { error, data } = result;
      if (error) handleCloseSchedule();
      if (!error) setProduct(data);
    }
    setLoading(false);
  };

  const getServiceAps = async () => {
    setLoading(true);
    const serviceId = hasServiceEnv();
    if (serviceId) {
      const result = await fetchServiceById(serviceId);
      const { error, data } = result;
      if (error) handleCloseSchedule();
      if (!error) setService(data);
    }
    setLoading(false);
  };

  const handleCloseSchedule = () => {
    clearData();
    setIsSchedulerOpen(false);
    onClose && onClose();
  };

  React.useEffect(() => {
    if (selectedItemToSchedule === 'PRODUCT_ENV') getVaccine();
    if (selectedItemToSchedule === 'SERVICE_ENV') getServiceAps();
    if (selectedItemToSchedule === 'PRODUCT' && selectedProduct) setProduct(selectedProduct);
    if (selectedItemToSchedule === 'SERVICE' && selectedService) setService(selectedService);
  }, [selectedItemToSchedule]);

  const handleTitleCloseButton = () => {
    if (history.length <= 1) {
      if (mainHistory.length > 1) return 'Voltar';
      return 'Fechar';
    }
    return 'Voltar';
  };

  const handleClosePage = () => {
    if (history.length <= 1) {
      if (mainHistory.length > 1) return onMainBack();
      return handleCloseSchedule();
    }
    return goBack();
  };

  function ServiceItemAddCartComponent() {
    return (
      <ServiceItemAddCart
        product={product}
        service={service}
        handleAddProductList={handleAddProductList}
        onClose={handleClosePage}
        titleMainButton="Agendar"
        titleSecondaryButton={handleTitleCloseButton()}
      />
    );
  }

  return (
    <>
      {product || service ? (
        <S.Container>
          <ServiceItemAddCartComponent />
        </S.Container>
      ) : null}
      <LoadingOverlay loading={loading} />
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => {
          setIsModalSystemUnavailableVisible(false);
        }}
      />
      <ModalGenericRules
        when={INFO_GENERIC_RULES_WHEN.after_patient_identification}
        condition={genericModal.genericModalCondition}
        visible={genericModal.modalGenericVisible}
        isVisible={genericModal.setModalGenericVisible}
        handleActionButton={genericModal.handleActionButtonModal}
      />
    </>
  );
};

export default ResourceDetails;
